import { html, unsafeCSS } from 'lit';
import { customElement, query } from 'lit/decorators.js';
import { WEB_COMPONENT_POLICY_PANEL_GENERAL_SECURITY_TELEMETRY } from '../../../../../../../webcomponents.constants';
import { GzPolicyPanelAccordionTemplateWebComponent } from '../../../../../../common/policyPanel/uiSettings/accordionTemplate';
import {
  GeneralSecurityTelemetryFormValue,
  GeneralSecurityTelemetrySearchableKeywords,
} from '../../../../utils/uiSettings/general/general.types';
import {
  FORMAT_TYPE_JSON,
  ModuleId,
  SectionId,
  SecurityTelemetryProtocol,
  SecurityTelemetryProxyType,
  SecurityTelemetrySiemType,
  SecurityTelemetryUrlValidity,
} from '@nirvana/shared/dtos/types/policies/common';
import {
  getGeneralSecurityTelemetryDefaultValue,
  getGeneralSecurityTelemetrySearchableKeywords,
  getSiemSolutionsTypes,
} from '../../../../utils/uiSettings/general/general.values';
import { FormGroupValue } from '../../../../../../../ui/gzForm/formGroup/types/form-group-value';
import { GzFormFieldWebComponent } from '../../../../../../../ui/gzForm/formField';
import { styles as specificAccordionStyles } from '../view/view.scss';
import { FormGroup } from 'apps/webcomponents/src/ui/gzForm/formGroup/form-group';
import { USE_EXT_JS_VERSION } from '../../../../config/config';
import _ from 'lodash';
import { PolicyPanelManager } from '../../../../PolicyPanelManager';
import deepmerge from 'deepmerge';
import { toggleDisabled, toggleVisibility } from '../../../../../../../utils/htmlElement.utils';
import { translateFn } from '../../../../../../../utils/translator.helper';
import { FontSizes, FontWeights } from '../../../../../../../types/common/types';
import { GzCheckboxWebComponent } from '../../../../../../../ui/gzCheckbox';
import { GzRadioGroupWebComponent } from '../../../../../../../ui/gzRadio';
import { InputWidthValues } from '../../../../../../../ui/gzInput';
import { GzDropdownWebComponent } from '../../../../../../../ui/gzDropdown';
import { GzInputWebComponent } from '../../../../../../../ui/gzInput/component/GzInputWebComponent';
import { GzHardCodedFormField } from '../../../../../../../ui/gzForm/formField/component/GzHardCodedFormField';
import {
  PolicyDependenciesContainer,
  PolicyTemplatesApiClients,
} from '../../../../../../common/policyPanel/uiSettings/dependencies/PolicyDependenciesContainer';
import { RequiredValidator } from 'apps/webcomponents/src/ui/gzForm/validators/required.validator';
import { HostnameValidator } from '../../../../../../../ui/gzForm/validators/hostname.validator';
import { RegexValidator } from 'apps/webcomponents/src/ui/gzForm/validators/regex.validator';
import { InputTypes } from 'apps/webcomponents/src/ui/gzInput/types';
import { PortValidator } from '../../../../../../../ui/gzForm/validators/port.validator';
import { RemoteValidator } from '../../../../../../../ui/gzForm/validators/remote.validator';
import { ValidationResult } from 'apps/webcomponents/src/ui/gzForm/formField/types/form-validator';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { store } from '@nirvana/redux/store';
import { SpinnerWidth } from 'apps/webcomponents/src/ui/gzProgressSpinner/types';
import { setIsWaitingValidation } from '../../../../redux/saveButtonSlice/actions.save';
import { CustomizableValidator } from 'apps/webcomponents/src/ui/gzForm/validators/customizable.validator';
import { DataLoadedStatus, PolicyAppState } from '../../../../redux/types';
import { getIsGeneralSecurityTelemetryValidationLoaded } from '../../../../redux/customStates/general/general.actions';
import { EventKeyManager } from '../../../../../../../utils/keys';
import { distinctUntilChanged } from 'rxjs/operators';

const REMOTE_VALIDATION_DELAY = 1000;
const SPINNER_WIDTH_VALUE = 24;

@customElement(WEB_COMPONENT_POLICY_PANEL_GENERAL_SECURITY_TELEMETRY)
export class GzGeneralSecurityTelemetryWebComponent extends GzPolicyPanelAccordionTemplateWebComponent<
  GeneralSecurityTelemetryFormValue,
  GeneralSecurityTelemetrySearchableKeywords,
  PolicyTemplatesApiClients
> {
  protected hasPendingValidationSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  protected hasPendingValidation$: Observable<boolean> = this.hasPendingValidationSubject.pipe(distinctUntilChanged());

  protected path = {
    moduleId: ModuleId.GENERAL,
    sectionId: SectionId.SECURITY_TELEMETRY,
  };

  protected SERVER_URL_VALIDITY_PATH = ['remoteValidationHistory', 'general', 'securityTelemetry'];

  protected searchableKeywords = getGeneralSecurityTelemetrySearchableKeywords();

  static get styles() {
    return [...GzPolicyPanelAccordionTemplateWebComponent.styles, unsafeCSS(`${specificAccordionStyles}`)];
  }
  private validationCounter = 0;
  private validationResult: ValidationResult = { valid: true };

  @query('#general-security-telemetry-toggle')
  private securityTelemetryToggle: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#general-security-telemetry-info-text')
  private securityTelemetryInfoText: HTMLElement;
  @query('#general-security-telemetry-content')
  private telemetryContent: HTMLElement;
  @query('#process-create-form-field')
  private processCreateFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#terminate-process-form-field')
  private terminateProcessFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#network-connection-form-field')
  private networkConnectionFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#logon-form-field')
  private logonFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#logoff-form-field')
  private logoutFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#create-file-form-field')
  private createFileFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#delete-file-form-field')
  private deleteFileFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#modify-file-form-field')
  private modifyFileFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#read-from-file-form-field')
  private readFromFileFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#move-file-form-field')
  private moveFileFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#create-key-form-field')
  private createKeyFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#delete-key-form-field')
  private deleteKeyFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#delete-value-form-field')
  private deleteValueFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#modify-value-form-field')
  private modifyValueFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#proxy-form-field')
  private proxyFormField: GzFormFieldWebComponent<GzRadioGroupWebComponent, string | number>;
  @query('#siem-solution-form-field')
  private siemFormField: GzFormFieldWebComponent<GzDropdownWebComponent, string | number>;
  @query('#server-url-form-field')
  private serverUrlFormField: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#token-form-field')
  private tokenFormField: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#port-form-field')
  private portFormField: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#host-form-field')
  private hostFormField: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#ignore-ssl-errors-form-field')
  private ignoreSslErrorsFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#allow-self-sign-cert-form-field')
  private allowSelfSignedCertificateFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#splunk-section')
  private splunkSection: HTMLElement;
  @query('#syslog-section')
  private syslogSection: HTMLElement;
  @query('#general-security-telemetry-communication')
  private communicationSection: HTMLElement;
  @query('#allow-self-sign-cert-section')
  private allowSelfSignCertSection: HTMLElement;

  @query('#ssl-info-box')
  private sslInfoBox: HTMLElement;

  private protocolFormField: GzHardCodedFormField<number>;
  @query('#url-spinner')
  private urlSpinner: HTMLElement;

  protected setClients() {
    this.clients = PolicyDependenciesContainer.getInstance().resolveDependency(
      'PolicyTemplates'
    ) as PolicyTemplatesApiClients;
  }

  protected async onKeyDown(event: KeyboardEvent) {
    if (!EventKeyManager.isPrintable(event)) {
      return;
    }
    const additionalInfo = PolicyPanelManager.getInstance().getAdditionalPolicyInfo(
      this.SERVER_URL_VALIDITY_PATH,
      null
    );
    if (additionalInfo && additionalInfo.value !== this.serverUrlFormField.value) {
      this.setLoadingValidationState(true);
    }
  }

  protected setSplunkSectionValidators(isVisible: boolean) {
    // validators should be applied only when the toggle is on and Splunk solution is selected
    if (isVisible) {
      this.tokenFormField.Validators = [
        RequiredValidator('Required field'),
        RegexValidator(
          /^(([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})|(\*{8})|)$/i,
          translateFn('Invalid token')
        ),
      ];
      this.serverUrlFormField.Validators = [CustomizableValidator(this.customServerUrlValidation.bind(this))];
    } else {
      this.tokenFormField.Validators = [];
      this.serverUrlFormField.Validators = [];
    }
  }
  private async customServerUrlValidation(value: string) {
    // increment the request counter to keep track of the current request
    const currentValidationCounter = this.validationCounter + 1;
    this.validationCounter = currentValidationCounter;

    const requiredValidatorResponse = await RequiredValidator(translateFn('Required field'))(value);

    // if the validationCounter has changed, it means that a new validation has been made and this one is no longer valid
    if (this.validationCounter > currentValidationCounter) {
      return this.validationResult;
    }

    // loading state is set to false in case the required validator fails
    // this is done to avoid the spinner to be displayed when the field is empty
    // the result of the required validator is returned and kept in the validationResult variable
    if (!requiredValidatorResponse.valid) {
      this.setLoadingValidationState(false);
      this.validationResult = requiredValidatorResponse;
      return this.validationResult;
    }
    const additionalInfo = PolicyPanelManager.getInstance().getAdditionalPolicyInfo(
      this.SERVER_URL_VALIDITY_PATH,
      null
    );
    if (additionalInfo && additionalInfo.value === value) {
      this.setLoadingValidationState(false);
      if (additionalInfo.serverUrlValidity === undefined) {
        return { valid: false, error: translateFn('A communication error has occurred. Please try again later.') };
      }
      return this.getValidityResponse(additionalInfo.serverUrlValidity);
    }

    this.hasPendingValidationSubject.next(true);
    // when remote validation is started, the loading state is set to true
    this.setLoadingValidationState(true);
    const remoteValidationResult = await RemoteValidator(
      (val: string) => of(this.remoteUrlValidation(val, currentValidationCounter)),
      (response: ValidationResult) => {
        return response.valid;
      },
      (response: { valid: false; error: string }) => {
        return response.error;
      },
      translateFn('A communication error has occurred. Please try again later.'),
      REMOTE_VALIDATION_DELAY
    )(value);

    // remote validation is done so the loading state is set to false
    this.setLoadingValidationState(false);

    // validationResult is updated with the result of the remote validation
    this.validationResult = remoteValidationResult;
    return this.validationResult;
  }

  private setLoadingValidationState(state: boolean) {
    store.dispatch(setIsWaitingValidation(state));
    toggleVisibility(this.urlSpinner, state);
  }

  private async verifySecurityTelemetryUrl(url: string) {
    const additionalInfo = PolicyPanelManager.getInstance().getAdditionalPolicyInfo(
      this.SERVER_URL_VALIDITY_PATH,
      null
    );
    if (additionalInfo && additionalInfo.value === url) {
      return additionalInfo.serverUrlValidity;
    }

    const result = (await this.clients.verifySecurityTelemetryUrl(url)).success;
    PolicyPanelManager.getInstance().setAdditionalPolicyInfoToPath(this.SERVER_URL_VALIDITY_PATH, {
      serverUrlValidity: result,
      value: url,
    });
    return result;
  }

  protected async remoteUrlValidation(value: string, currentRequestCounter: number, response?: number) {
    try {
      const urlValidity = await this.verifySecurityTelemetryUrl(value);

      // if the validationCounter has changed, it means that a new validation has been made and this one is no longer valid
      if (this.validationCounter > currentRequestCounter) {
        return this.validationResult;
      }

      return this.getValidityResponse(urlValidity);
    } catch (e) {
      console.error(e);
      return {
        valid: false,
        error: translateFn(
          "The SSL certificate could not be verified with the Certificate Authority (CA) or the server's DNS."
        ),
      };
    }
  }

  private getValidityResponse(urlValidity?: number) {
    // if the url is not valid, the bypass validation checkbox is displayed
    toggleVisibility(this.allowSelfSignCertSection, urlValidity === SecurityTelemetryUrlValidity.INVALID_FAILED);

    if (urlValidity === SecurityTelemetryUrlValidity.VALID) {
      if (this.allowSelfSignedCertificateFormField.value) {
        this.allowSelfSignedCertificateFormField.value = false;
      }
      return { valid: true };
    }

    //checkbox is true and validation has failed
    if (this.allowSelfSignedCertificateFormField.value && urlValidity === SecurityTelemetryUrlValidity.INVALID_FAILED) {
      return { valid: true };
    }

    if (urlValidity === SecurityTelemetryUrlValidity.INVALID_TLS) {
      if (this.allowSelfSignedCertificateFormField.value) {
        this.allowSelfSignedCertificateFormField.value = false;
      }
      return {
        valid: false,
        error: translateFn(
          'The web server at this URL does not support TLS 1.2. Please ensure the server is configured to support TLS 1.2 or higher.'
        ),
      };
    }
    // INVALID_FALSE case
    return {
      valid: false,
      error: translateFn(
        "The SSL certificate could not be verified with the Certificate Authority (CA) or the server's DNS."
      ),
    };
  }

  handleBypassCheckboxChange(event: CustomEvent) {
    toggleVisibility(this.sslInfoBox, event.detail.checked);
    this.serverUrlFormField.validate(true);
  }

  protected setSyslogSectionValidators(isVisible: boolean) {
    // validators should be applied only when the toggle is on and Syslog solution is selected
    if (isVisible) {
      this.hostFormField.Validators = [RequiredValidator('Required field'), HostnameValidator('Invalid hostname')];
      this.portFormField.Validators = [RequiredValidator('Required field'), PortValidator('Invalid port value')];
    } else {
      this.hostFormField.Validators = [];
      this.portFormField.Validators = [];
    }
  }

  protected setValidators() {
    const activateSplunkValidators =
      this.valueT?.uiSettings.general.securityTelemetry.siem === SecurityTelemetrySiemType.SPLUNK &&
      this.valueT?.uiSettings.general.securityTelemetry.enabled;
    const activateSyslogValidators =
      this.valueT?.uiSettings.general.securityTelemetry.siem === SecurityTelemetrySiemType.SYSLOG &&
      this.valueT?.uiSettings.general.securityTelemetry.enabled;

    this.setSplunkSectionValidators(activateSplunkValidators);
    this.setSyslogSectionValidators(activateSyslogValidators);

    // subject should be changed only after the name validation is finished
    this.serverUrlFormField.valid$.subscribe(valid => {
      this.hasPendingValidationSubject.next(false);
    });
  }

  protected createFormGroup(): void {
    this.protocolFormField = new GzHardCodedFormField<number>(
      this.siemFormField.value === SecurityTelemetrySiemType.SPLUNK
        ? SecurityTelemetryProtocol.HTTP
        : SecurityTelemetryProtocol.TLS
    );
    this.formGroup = FormGroup.build({
      uiSettings: {
        general: {
          securityTelemetry: {
            enabled: this.securityTelemetryToggle,
            allowSelfSignedCertificate: this.allowSelfSignedCertificateFormField,
            siem: this.siemFormField,
            config: {
              hostname: this.hostFormField,
              port: this.portFormField,
              key: this.tokenFormField,
              url: this.serverUrlFormField,
              ignoreSSLErrors: this.ignoreSslErrorsFormField,
            },
            eventTypes: {
              createFile: this.createFileFormField,
              createProcess: this.processCreateFormField,
              deleteFile: this.deleteFileFormField,
              logon: this.logonFormField,
              logout: this.logoutFormField,
              modifyFile: this.modifyFileFormField,
              moveFile: this.moveFileFormField,
              networkConnection: this.networkConnectionFormField,
              readFromFile: this.readFromFileFormField,
              regCreateKey: this.createKeyFormField,
              regDeleteKey: this.deleteKeyFormField,
              regDeleteValue: this.deleteValueFormField,
              regModifyValue: this.modifyValueFormField,
              terminateProcess: this.terminateProcessFormField,
            },
            format: new GzHardCodedFormField<number>(FORMAT_TYPE_JSON),
            protocol: this.protocolFormField,
            proxy: {
              profile: this.proxyFormField,
            },
          },
        },
      },
    });
  }

  protected getFormGroupValue(): FormGroupValue | null {
    if (!this.formGroup) {
      return null;
    }

    let value = _.cloneDeep(this.formGroup.value);

    if (USE_EXT_JS_VERSION) {
      value = _.get(value, ['uiSettings', 'general', 'securityTelemetry']);
    }
    return value;
  }

  protected getValueFromManager(): GeneralSecurityTelemetryFormValue {
    const defaultValue = {
      uiSettings: {
        [ModuleId.GENERAL]: {
          [SectionId.SECURITY_TELEMETRY]: getGeneralSecurityTelemetryDefaultValue(),
        },
      },
    };
    const value = {
      uiSettings: {
        [ModuleId.GENERAL]: {
          [SectionId.SECURITY_TELEMETRY]: PolicyPanelManager.getInstance().getPolicy(
            'uiSettings.general.securityTelemetry'
          ),
        },
      },
    };

    return deepmerge(defaultValue, value);
  }

  /**
   * Render specific title.
   */
  protected renderTitle() {
    return html`<gz-form-field id="general-security-telemetry-toggle" status-field>
      <gz-toggle
        class="content"
        searchable-label="${this.searchableKeywords[GeneralSecurityTelemetrySearchableKeywords.SecurityTelemetry]
          .label}"
        gz-form-input
        @toggleChange="${this.onToggleChange}"
      >
        <span
          >${this.searchableKeywords[GeneralSecurityTelemetrySearchableKeywords.SecurityTelemetry]
            .translatedLabel}</span
        >
      </gz-toggle>
    </gz-form-field>`;
  }

  /**
   * Render specific info text.
   */
  protected renderInfoText() {
    return html`
      <gz-text
        id="general-security-telemetry-info-text"
        fontSize="${FontSizes.PARAGRAPH_12}"
        fontWeight="${FontWeights.NORMAL}"
        class="infoText"
        >${translateFn(
          'Export security event raw data from protected endpoints to SIEM solutions for advanced analysis and correlation. Duplicate data is filtered to optimize performance and reduce export size.'
        )}
      </gz-text>
    `;
  }

  /**
   * Render specific content.
   */
  protected renderContent() {
    return html`
      <div id="general-security-telemetry-content">
        ${this.renderSiemConnectionSettings()} ${this.renderEventTypes()}
        ${this.renderCommunicationBetweenEndpointsAndSiems()}
      </div>
    `;
  }

  protected renderCommunicationBetweenEndpointsAndSiems() {
    return html`
      <div id="general-security-telemetry-communication" class="hidden mt-4">
        <gz-title
          class="form-subtitle"
          searchable-label="${this.searchableKeywords[
            GeneralSecurityTelemetrySearchableKeywords.CommunicationBetweenEndpointsSIEM
          ].label}"
          fontWeight="${FontWeights.SEMIBOLD}"
          fontSize="${FontSizes.PARAGRAPH_11}"
        >
          ${this.searchableKeywords[GeneralSecurityTelemetrySearchableKeywords.CommunicationBetweenEndpointsSIEM]
            .translatedLabel}
        </gz-title>
        <gz-form-field id="proxy-form-field">
          <gz-radio-group gz-form-input>
            <gz-radio
              class="mt-3"
              .value="${SecurityTelemetryProxyType.ENABLE}"
              label="${this.searchableKeywords[GeneralSecurityTelemetrySearchableKeywords.ProxyConfigured]
                .translatedLabel}"
              searchable-label="${this.searchableKeywords[GeneralSecurityTelemetrySearchableKeywords.ProxyConfigured]
                .label}"
            ></gz-radio>
            <gz-radio
              .value="${SecurityTelemetryProxyType.DISABLE}"
              class="mt-2"
              label="${this.searchableKeywords[GeneralSecurityTelemetrySearchableKeywords.DoNotUseProxy]
                .translatedLabel}"
              searchable-label="${this.searchableKeywords[GeneralSecurityTelemetrySearchableKeywords.DoNotUseProxy]
                .label}"
            ></gz-radio>
          </gz-radio-group>
        </gz-form-field>
      </div>
    `;
  }

  protected renderSiemConnectionSettings() {
    return html`
      <div id="general-security-telemetry-siem-connection-settings">
        <gz-title
          class="form-subtitle mt-4"
          searchable-label="${
            this.searchableKeywords[GeneralSecurityTelemetrySearchableKeywords.SiemConnectionSettings].label
          }"
          fontWeight="${FontWeights.SEMIBOLD}"
          fontSize="${FontSizes.PARAGRAPH_11}"
        >
          ${this.searchableKeywords[GeneralSecurityTelemetrySearchableKeywords.SiemConnectionSettings].translatedLabel}
        </gz-title>
        <gz-text fontSize="${FontSizes.PARAGRAPH_12}" fontWeight="${FontWeights.NORMAL}" class="infoText mt-1"
          >${translateFn('Select and provide the details of the SIEM solution where to export the data.')}</gz-text
        >
        <gz-form-field
          class="label-mw-250 mt-2"
          id="siem-solution-form-field"
          label="${this.searchableKeywords[GeneralSecurityTelemetrySearchableKeywords.SiemSolution].translatedLabel}"
          searchable-label="${this.searchableKeywords[GeneralSecurityTelemetrySearchableKeywords.SiemSolution].label}"
        >
          <gz-dropdown
            gz-form-input
            labelWidth="${InputWidthValues.LARGE}"
            .options="${getSiemSolutionsTypes()}"
            @optionSelected=${this.handleSolutionChanged}
          >
          </gz-dropdown>
        </gz-form-field>
        <div id="splunk-section" class="hidden">

            <gz-form-field
              class="label-mw-250 siem-form-field"
              id="server-url-form-field"
              label="${this.searchableKeywords[GeneralSecurityTelemetrySearchableKeywords.ServerUrl].translatedLabel}"
              searchable-label="${this.searchableKeywords[GeneralSecurityTelemetrySearchableKeywords.ServerUrl].label}"
              @change="${this.handleServerUrlChange}"
              @keydown="${this.onKeyDown}"
              required
            >
              <div id="serverUrl">
              <gz-input
                gz-form-input
                inputWidth="${InputWidthValues.LARGE}"
                placeholder="${translateFn('Server URL')}"
              >

              </gz-input>
                <gz-progress-spinner
                  id="url-spinner"
                  spinnerWidth="${SpinnerWidth.CUSTOM}"
                  customSpinnerWidthValue="${SPINNER_WIDTH_VALUE}"
                  class="hidden"
                >
              </div>
              <gz-input-error></gz-input-error>
            </gz-form-field>
            </gz-progress-spinner>

          <div id="allow-self-sign-cert-section" class="hidden">
            <gz-form-field id="allow-self-sign-cert-form-field">
              <gz-checkbox
                gz-form-input
                id="bypass-validation-checkbox"
                fontWeight="${FontWeights.NORMAL}"
                fontSize="${FontSizes.PARAGRAPH_11}"
                label="${translateFn('Bypass validation of the SSL certificate on HTTP collector')}"
                @change="${this.handleBypassCheckboxChange}"
              >
              </gz-checkbox>
            </gz-form-field>
            <gz-info-box severity="warning" id="ssl-info-box" class="hidden mb-2" id="ssl-info-box">
              <span class="warning-text">
                ${translateFn(
                  'Using an untrusted or misconfigured SSL certificate is a risk to your network. You are strongly advised to replace the SSL certificate with one that meets the security requirements.'
                )}
              </span>
            </gz-info-box>
          </div>
          <gz-form-field
            class="label-mw-250"
            id="token-form-field"
            label="${this.searchableKeywords[GeneralSecurityTelemetrySearchableKeywords.Token].translatedLabel}"
            searchable-label="${this.searchableKeywords[GeneralSecurityTelemetrySearchableKeywords.Token].label}"
            required
          >
            <gz-input
              gz-form-input
              inputWidth="${InputWidthValues.LARGE}"
              placeholder="${translateFn('Authorization token')}"
              type="${InputTypes.PASSWORD}"
            >
            </gz-input>
            <gz-input-error></gz-input-error>
          </gz-form-field>
        </div>
        <div id="syslog-section" class="hidden">
          <gz-form-field
            class="label-mw-250 mt-2 siem-form-field"
            id="host-form-field"
            label="${this.searchableKeywords[GeneralSecurityTelemetrySearchableKeywords.HostnameIp].translatedLabel}"
            searchable-label="${this.searchableKeywords[GeneralSecurityTelemetrySearchableKeywords.HostnameIp].label}"
            required
          >
            <gz-input
              gz-form-input
              inputWidth="${InputWidthValues.LARGE}"
              placeholder="${translateFn('Hostname or IP address')}"
            >
            </gz-input>
            <gz-input-error></gz-input-error>
          </gz-form-field>
          <gz-form-field
            class="label-mw-250 mt-2"
            id="port-form-field"
            label="${this.searchableKeywords[GeneralSecurityTelemetrySearchableKeywords.Port].translatedLabel}"
            searchable-label="${this.searchableKeywords[GeneralSecurityTelemetrySearchableKeywords.Port].label}"
            required
          >
            <gz-input
              gz-form-input
              inputWidth="${InputWidthValues.LARGE}"
              placeholder="${translateFn('Port number')}"
            >
            </gz-input>
            <gz-input-error></gz-input-error>
          </gz-form-field>
          <gz-form-field id="ignore-ssl-errors-form-field" class="mt-2">
            <gz-checkbox
              gz-form-input
              fontWeight="${FontWeights.NORMAL}"
              fontSize="${FontSizes.PARAGRAPH_11}"
              label="${
                this.searchableKeywords[GeneralSecurityTelemetrySearchableKeywords.IgnoreSslErrors].translatedLabel
              }"
              searchable-label="${
                this.searchableKeywords[GeneralSecurityTelemetrySearchableKeywords.IgnoreSslErrors].label
              }"
            >
            </gz-checkbox>
          </gz-form-field>
        </div>
      </div>
    `;
  }

  protected renderEventTypes() {
    return html`
      <div id="general-security-telemetry-event-types" class="mt-4">
        <div id="general-security-telemetry-siem-connection-settings">
          <gz-title
            class="form-subtitle"
            searchable-label="${this.searchableKeywords[GeneralSecurityTelemetrySearchableKeywords.EventTypes].label}"
            fontWeight="${FontWeights.SEMIBOLD}"
            fontSize="${FontSizes.PARAGRAPH_11}"
          >
            ${this.searchableKeywords[GeneralSecurityTelemetrySearchableKeywords.EventTypes].translatedLabel}
          </gz-title>
          <gz-text fontSize="${FontSizes.PARAGRAPH_12}" fontWeight="${FontWeights.NORMAL}" class="infoText mt-1"
            >${translateFn('Select the types of events to export to SIEM:')}</gz-text
          >
        </div>
        <div id="security-telemetry-events">
          <div id="col-1" class="event-column">
            <gz-form-field id="process-create-form-field" class="mt-2">
              <gz-checkbox
                gz-form-input
                fontWeight="${FontWeights.NORMAL}"
                fontSize="${FontSizes.PARAGRAPH_11}"
                label="${
                  this.searchableKeywords[GeneralSecurityTelemetrySearchableKeywords.ProcessCreate].translatedLabel
                }"
                searchable-label="${
                  this.searchableKeywords[GeneralSecurityTelemetrySearchableKeywords.ProcessCreate].label
                }"
              >
              </gz-checkbox>
            </gz-form-field>
            <gz-form-field id="terminate-process-form-field" class="mt-2">
              <gz-checkbox
                gz-form-input
                fontWeight="${FontWeights.NORMAL}"
                fontSize="${FontSizes.PARAGRAPH_11}"
                searchable-label="${
                  this.searchableKeywords[GeneralSecurityTelemetrySearchableKeywords.TerminateProcess].label
                }"
                label="${
                  this.searchableKeywords[GeneralSecurityTelemetrySearchableKeywords.TerminateProcess].translatedLabel
                }"
              >
              </gz-checkbox>
            </gz-form-field>
            <gz-form-field id="network-connection-form-field" class="mt-2">
              <gz-checkbox
                gz-form-input
                fontWeight="${FontWeights.NORMAL}"
                fontSize="${FontSizes.PARAGRAPH_11}"
                label="${
                  this.searchableKeywords[GeneralSecurityTelemetrySearchableKeywords.NetworkConnection].translatedLabel
                }"
                searchable-label="${
                  this.searchableKeywords[GeneralSecurityTelemetrySearchableKeywords.NetworkConnection].label
                }"
              >
              </gz-checkbox>
            </gz-form-field>
            <gz-form-field id="logon-form-field" class="mt-2">
              <gz-checkbox
                gz-form-input
                fontWeight="${FontWeights.NORMAL}"
                fontSize="${FontSizes.PARAGRAPH_11}"
                label="${this.searchableKeywords[GeneralSecurityTelemetrySearchableKeywords.Logon].translatedLabel}"
                searchable-label="${this.searchableKeywords[GeneralSecurityTelemetrySearchableKeywords.Logon].label}"
              >
              </gz-checkbox>
            </gz-form-field>
            <gz-form-field id="logoff-form-field" class="mt-2">
              <gz-checkbox
                gz-form-input
                fontWeight="${FontWeights.NORMAL}"
                fontSize="${FontSizes.PARAGRAPH_11}"
                label="${this.searchableKeywords[GeneralSecurityTelemetrySearchableKeywords.Logoff].translatedLabel}"
                searchable-label="${this.searchableKeywords[GeneralSecurityTelemetrySearchableKeywords.Logoff].label}"
              >
              </gz-checkbox>
            </gz-form-field>
          </div>
          <div id="col-2" class="event-column">
            <gz-form-field id="create-file-form-field" class="mt-2">
              <gz-checkbox
                gz-form-input
                fontWeight="${FontWeights.NORMAL}"
                fontSize="${FontSizes.PARAGRAPH_11}"
                label="${this.searchableKeywords[GeneralSecurityTelemetrySearchableKeywords.CreatFile].translatedLabel}"
                searchable-label="${
                  this.searchableKeywords[GeneralSecurityTelemetrySearchableKeywords.CreatFile].label
                }"
              >
              </gz-checkbox>
            </gz-form-field>
            <gz-form-field
              id="delete-file-form-field"
              class="mt-2""
            >
              <gz-checkbox
                gz-form-input
                fontWeight="${FontWeights.NORMAL}"
                fontSize="${FontSizes.PARAGRAPH_11}"
                label="${
                  this.searchableKeywords[GeneralSecurityTelemetrySearchableKeywords.DeleteFile].translatedLabel
                }"
                searchable-label="${
                  this.searchableKeywords[GeneralSecurityTelemetrySearchableKeywords.DeleteFile].label
                }"
              >
              </gz-checkbox>
            </gz-form-field>
            <gz-form-field
              id="modify-file-form-field"
              class="mt-2"
            >
              <gz-checkbox
                gz-form-input
                fontWeight="${FontWeights.NORMAL}"
                fontSize="${FontSizes.PARAGRAPH_11}"
                label="${
                  this.searchableKeywords[GeneralSecurityTelemetrySearchableKeywords.ModifyFile].translatedLabel
                }"
                searchable-label="${
                  this.searchableKeywords[GeneralSecurityTelemetrySearchableKeywords.ModifyFile].label
                }"

              >
              </gz-checkbox>
            </gz-form-field>
            <gz-form-field
              id="read-from-file-form-field"
              class="mt-2"

            >
              <gz-checkbox
                gz-form-input
                fontWeight="${FontWeights.NORMAL}"
                fontSize="${FontSizes.PARAGRAPH_11}"
                label="${
                  this.searchableKeywords[GeneralSecurityTelemetrySearchableKeywords.ReadFromFile].translatedLabel
                }"
                searchable-label="${
                  this.searchableKeywords[GeneralSecurityTelemetrySearchableKeywords.ReadFromFile].label
                }"
              >
              </gz-checkbox>
            </gz-form-field>
            <gz-form-field
              id="move-file-form-field"
              class="mt-2"

            >
              <gz-checkbox
                gz-form-input
                fontWeight="${FontWeights.NORMAL}"
                fontSize="${FontSizes.PARAGRAPH_11}"
                label="${this.searchableKeywords[GeneralSecurityTelemetrySearchableKeywords.MoveFile].translatedLabel}"
                searchable-label="${this.searchableKeywords[GeneralSecurityTelemetrySearchableKeywords.MoveFile].label}"
              >
              </gz-checkbox>
            </gz-form-field>
          </div>
          <div id="col-3" class="event-column">
            <gz-form-field
              id="create-key-form-field"
              class="mt-2"

            >
              <gz-checkbox
                gz-form-input
                fontWeight="${FontWeights.NORMAL}"
                fontSize="${FontSizes.PARAGRAPH_11}"
                label="${
                  this.searchableKeywords[GeneralSecurityTelemetrySearchableKeywords.RegistryCreateKey].translatedLabel
                }"
                searchable-label="${
                  this.searchableKeywords[GeneralSecurityTelemetrySearchableKeywords.RegistryCreateKey].label
                }"
              >
              </gz-checkbox>
            </gz-form-field>
            <gz-form-field
              id="delete-key-form-field"
              class="mt-2"

            >
              <gz-checkbox
                gz-form-input
                fontWeight="${FontWeights.NORMAL}"
                fontSize="${FontSizes.PARAGRAPH_11}"
                label="${
                  this.searchableKeywords[GeneralSecurityTelemetrySearchableKeywords.RegistryDeleteKey].translatedLabel
                }"
                searchable-label="${
                  this.searchableKeywords[GeneralSecurityTelemetrySearchableKeywords.RegistryDeleteKey].label
                }"
              >
              </gz-checkbox>
            </gz-form-field>
            <gz-form-field
              id="delete-value-form-field"
              class="mt-2"
            >
              <gz-checkbox
                gz-form-input
                fontWeight="${FontWeights.NORMAL}"
                fontSize="${FontSizes.PARAGRAPH_11}"
                label="${
                  this.searchableKeywords[GeneralSecurityTelemetrySearchableKeywords.RegistryDeleteValue]
                    .translatedLabel
                }"
                searchable-label="${
                  this.searchableKeywords[GeneralSecurityTelemetrySearchableKeywords.RegistryDeleteValue].label
                }"
              >
              </gz-checkbox>
            </gz-form-field>
            <gz-form-field
              id="modify-value-form-field"
              class="mt-2"
            >
              <gz-checkbox
                gz-form-input
                fontWeight="${FontWeights.NORMAL}"
                fontSize="${FontSizes.PARAGRAPH_11}"
                label="${
                  this.searchableKeywords[GeneralSecurityTelemetrySearchableKeywords.RegistryModifyValue]
                    .translatedLabel
                }"
                searchable-label="${
                  this.searchableKeywords[GeneralSecurityTelemetrySearchableKeywords.RegistryModifyValue].label
                }"
              >
              </gz-checkbox>
            </gz-form-field>
          </div>
        </div>
      </div>
    `;
  }

  protected setFormData(): void {
    if (!this.valueT) {
      return;
    }
    this.formGroup.value = {
      uiSettings: {
        general: {
          securityTelemetry: this.valueT.uiSettings.general.securityTelemetry,
        },
      },
    };
  }

  private handleServerUrlChange(event: CustomEvent) {
    if (event.detail.sourceEvent) {
      return;
    }

    // set the bypass validation checkbox to false when the server URL is empty
    toggleVisibility(this.allowSelfSignCertSection, false);
    this.allowSelfSignedCertificateFormField.value = false;
  }

  async onToggleChange(event: CustomEvent) {
    // wait for values to be updated
    await this.updateComplete;

    toggleDisabled(this.telemetryContent, !event.detail.checked);
    toggleDisabled(this.securityTelemetryInfoText, !event.detail.checked);

    this.setSplunkSectionValidators(
      event.detail.checked && this.siemFormField.value === SecurityTelemetrySiemType.SPLUNK
    );
    this.setSyslogSectionValidators(
      event.detail.checked && this.siemFormField.value === SecurityTelemetrySiemType.SYSLOG
    );

    if (!event.detail.checked) {
      this.serverUrlFormField.value = '';
      this.tokenFormField.value = '';
      this.hostFormField.value = '';
      this.portFormField.value = '';
      this.allowSelfSignedCertificateFormField.value = false;
      toggleVisibility(this.allowSelfSignCertSection, false);
    }
  }

  private async handleSolutionChanged(event: CustomEvent) {
    // wait for values to be updated
    await this.updateComplete;

    const solution = event.detail.newValue.value;
    this.protocolFormField.value =
      solution === SecurityTelemetrySiemType.SPLUNK ? SecurityTelemetryProtocol.HTTP : SecurityTelemetryProtocol.TLS;

    const isSplunkVisible = solution === SecurityTelemetrySiemType.SPLUNK;
    const isSyslogVisible = solution === SecurityTelemetrySiemType.SYSLOG;

    toggleVisibility(this.splunkSection, isSplunkVisible);
    toggleVisibility(this.communicationSection, isSplunkVisible);
    toggleVisibility(this.syslogSection, isSyslogVisible);

    // reset values
    if (isSplunkVisible) {
      this.hostFormField.value = '';
      this.portFormField.value = '';
      this.ignoreSslErrorsFormField.value = false;
    }
    if (isSyslogVisible) {
      this.tokenFormField.value = '';
      this.serverUrlFormField.value = '';
      this.allowSelfSignedCertificateFormField.value = false;
    }

    this.setSplunkSectionValidators(isSplunkVisible && this.securityTelemetryToggle.value);
    this.setSyslogSectionValidators(isSyslogVisible && this.securityTelemetryToggle.value);
  }

  protected getAdditionalInfoFromManager(): any {
    return PolicyPanelManager.getInstance().getAdditionalPolicyInfo(this.SERVER_URL_VALIDITY_PATH, null);
  }

  protected isAdditionalInfoLoaded(policiesApp: PolicyAppState): boolean {
    return getIsGeneralSecurityTelemetryValidationLoaded(policiesApp) !== DataLoadedStatus.NOT_LOADED;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_SECURITY_TELEMETRY: GzGeneralSecurityTelemetryWebComponent;
  }
}
